<script>
export default {
  name: "VvPointsCard",
  props: {
    profile: {
      type: Object,
      default: () => ({})
    },
    dataTest: {
      type: String,
      default: "vv-points-card"
    },
    /**
     * flat = compact single-row variant (used in /reward, /point pages)
     * default = full 2-half glass card with progress ring (used in home)
     */
    flat: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    memberLevel() {
      return (
        this.profile?.member_list?.find(
          a => a.id == this.profile.member_level_id
        ) || {}
      );
    },
    totalPoint() {
      return this.numeralFormat(this.profile.total_point || 0);
    },
    expiringPoint() {
      return this.profile.point_expired || 0;
    },
    expiringPointText() {
      return this.numeralFormat(this.expiringPoint);
    },
    expiredDate() {
      if (!this.profile.point_expired_date) return null;
      return this.$dateFormat(this.profile.point_expired_date);
    },
    expiredDaysLeft() {
      if (!this.profile.point_expired_date) return null;
      const target = this.$moment(this.profile.point_expired_date);
      const today = this.$moment();
      const diff = target.diff(today, "days");
      return diff > 0 ? diff : 0;
    },
    tierName() {
      return (
        this.memberLevel.member_level_language?.[0]?.display ||
        this.memberLevel.name ||
        this.profile.member_level_name ||
        this.profile.tier_name ||
        this.$t("member_tier")
      );
    },
    tierBgColor() {
      return this.memberLevel.bg_color || "var(--primary-color)";
    },
    tierFontColor() {
      return (
        (this.memberLevel.font_color || "").trim() || "var(--primary-color)"
      );
    },
    tierIcon() {
      return this.memberLevel.icon || "";
    },
    /**
     * Flat variant background — modern gradient ผูกกับสี tier ของ member
     * (ก่อนหน้านี้ flat การ์ดใช้ --crm-card-glass-background สีขาวล้วน)
     */
    flatCardStyle() {
      const base = this.tierBgColor;
      return {
        "--vv-points-card-flat-start": `color-mix(in srgb, ${base} 85%, white)`,
        "--vv-points-card-flat-end": `color-mix(in srgb, ${base} 88%, black)`
      };
    },
    /**
     * สัดส่วนความคืบหน้าไปยัง next tier (0-1)
     * - upgrade case: total_spend / upgrade_baht
     * - maintain case: total_spend / maintain_baht
     * - ถึงเป้าแล้ว: 1 (ring เต็ม)
     */
    tierProgress() {
      const ml = this.memberLevel;
      if (!ml || !ml.id) return 0;

      const spend = Number(this.profile.total_spend) || 0;
      let target = 0;

      if (ml.upgrade_tier_id && Number(ml.upgrade_baht || 0) > 0) {
        target = Number(ml.upgrade_baht);
      } else if (ml.is_no_maintain == 0 && Number(ml.maintain_baht || 0) > 0) {
        target = Number(ml.maintain_baht);
      } else {
        return 1;
      }

      const ratio = target > 0 ? spend / target : 0;
      return Math.min(Math.max(ratio, 0), 1);
    },
    tierRingDash() {
      // circumference = 2 * PI * r (r = 24 in viewBox 56x56)
      const circumference = 2 * Math.PI * 24;
      const filled = circumference * this.tierProgress;
      return `${filled} ${circumference}`;
    },
    /**
     * เงื่อนไข: mirror จาก src/components/layout/ProfileTierLayout.vue
     * - Case upgrade: มี upgrade_tier_id และยังใช้ไม่ถึง upgrade_baht
     * - Case maintain: is_no_maintain == 0 และยังใช้ไม่ถึง maintain_baht
     * - ถ้าถึงเป้าแล้วทั้งคู่ = ซ่อน section
     */
    nextTierMessage() {
      const ml = this.memberLevel;
      if (!ml || !ml.id) return null;

      const spend = Number(this.profile.total_spend) || 0;

      if (ml.upgrade_tier_id) {
        const remaining = Number(ml.upgrade_baht || 0) - spend;
        if (remaining > 0) {
          return {
            type: "upgrade",
            remaining: this.numeralFormat(remaining),
            tierName: ml.upgrade_tier_name || this.$t("next_tier")
          };
        }
      }

      if (ml.is_no_maintain == 0 && Number(ml.maintain_baht || 0) > 0) {
        const remaining = Number(ml.maintain_baht || 0) - spend;
        if (remaining > 0) {
          return {
            type: "maintain",
            remaining: this.numeralFormat(remaining),
            tierName:
              ml.member_level_language?.[0]?.display ||
              ml.name ||
              this.$t("member_tier")
          };
        }
      }

      return null;
    }
  },
  methods: {
    goPoint() {
      this.$router.push("/point");
    },
    goTier() {
      if (!this.profile.member_level_id) return;
      this.$router.push("/member/" + this.profile.member_level_id);
    },
    goRedeem() {
      this.$router.push("/reward");
    }
  }
};
</script>

<template>
  <!-- Flat variant: compact single-row (reward/point pages) -->
  <section
    v-if="flat"
    class="vv-points-card vv-points-card--flat"
    :style="flatCardStyle"
    :data-test="dataTest"
  >
    <div class="vv-points-card__flat-main">
      <div
        class="vv-points-card__points-row"
        @click="goPoint"
        :data-test="`${dataTest}-total`"
      >
        <span class="vv-points-card__number f-bold">{{ totalPoint }}</span>
        <span class="vv-points-card__unit">{{ $t("currency_unit") }}</span>
        <font-awesome-icon
          icon="chevron-right"
          class="vv-points-card__chevron"
        />
      </div>
      <div
        v-if="expiringPoint > 0"
        class="vv-points-card__flat-expiry"
        :data-test="`${dataTest}-expiry`"
      >
        {{ expiringPointText }} {{ $t("currency_unit") }}
        {{ $t("use_until") || "ใช้ได้ถึง" }} {{ expiredDate }}
      </div>
    </div>

    <div
      class="vv-points-card__flat-tier"
      @click="goTier"
      :data-test="`${dataTest}-tier`"
    >
      <img
        v-if="tierIcon"
        :src="tierIcon"
        class="vv-points-card__flat-tier-icon"
        alt=""
      />
      <span class="vv-points-card__flat-tier-name">
        {{ tierName }}
      </span>
    </div>
    <font-awesome-icon
      icon="chevron-right"
      class="vv-points-card__flat-tier-chevron"
    />
  </section>

  <!-- Default variant: full 2-half glass card with progress ring (home) -->
  <section v-else class="vv-points-card" :data-test="dataTest">
    <div class="vv-points-card__top">
      <div class="vv-points-card__main">
        <div
          class="vv-points-card__points-row"
          @click="goPoint"
          :data-test="`${dataTest}-total`"
        >
          <span class="vv-points-card__number">{{ totalPoint }}</span>
          <span class="vv-points-card__unit">{{ $t("currency_unit") }}</span>
          <font-awesome-icon
            icon="chevron-right"
            class="vv-points-card__chevron"
          />
        </div>

        <div
          v-if="nextTierMessage"
          class="vv-points-card__tier-hint"
          :data-test="`${dataTest}-tier-hint`"
        >
          <template v-if="nextTierMessage.type === 'upgrade'">
            <span>{{ $t("more_spending") }}&nbsp;</span>
            <span class="vv-points-card__tier-hint-amount">
              {{ nextTierMessage.remaining }} {{ $t("upgrade_unit") }}
            </span>
            <span>&nbsp;{{ $t("to_upgrade") }}&nbsp;</span>
            <span class="vv-points-card__tier-hint-name">
              {{ nextTierMessage.tierName }}
            </span>
          </template>
          <template v-else>
            <span>{{ $t("more_spending") }}&nbsp;</span>
            <span class="vv-points-card__tier-hint-amount">
              {{ nextTierMessage.remaining }} {{ $t("upgrade_unit") }}
            </span>
            <span>&nbsp;{{ $t("keep_current") }}&nbsp;</span>
            <span class="vv-points-card__tier-hint-name">
              {{ nextTierMessage.tierName }}
            </span>
          </template>
        </div>
      </div>

      <div
        class="vv-points-card__tier"
        @click="goTier"
        :data-test="`${dataTest}-tier`"
      >
        <div class="vv-points-card__tier-ring">
          <svg
            class="vv-points-card__tier-ring-svg"
            viewBox="0 0 56 56"
            aria-hidden="true"
          >
            <circle
              class="vv-points-card__tier-ring-track"
              cx="28"
              cy="28"
              r="24"
              fill="none"
              stroke-width="6"
            />
            <circle
              class="vv-points-card__tier-ring-progress"
              cx="28"
              cy="28"
              r="24"
              fill="none"
              stroke-width="6"
              stroke-linecap="round"
              :stroke="tierBgColor"
              :stroke-dasharray="tierRingDash"
              transform="rotate(-90 28 28)"
            />
          </svg>
          <img
            v-if="tierIcon"
            :src="tierIcon"
            :alt="tierName"
            class="vv-points-card__tier-icon"
            :data-test="`${dataTest}-tier-icon`"
          />
          <span
            v-else
            class="vv-points-card__tier-name"
            :style="{ color: tierFontColor }"
            :title="tierName"
          >
            {{ tierName }}
          </span>
        </div>
        <font-awesome-icon
          icon="chevron-right"
          class="vv-points-card__tier-chevron"
        />
      </div>
    </div>

    <div class="vv-points-card__bottom">
      <div
        v-if="expiringPoint > 0"
        class="vv-points-card__expiry"
        :data-test="`${dataTest}-expiry`"
      >
        <span class="vv-points-card__expiry-num">{{ expiringPointText }}</span>
        <span class="vv-points-card__coin">
          <font-awesome-icon icon="coins" />
        </span>
        <span class="vv-points-card__expiry-text">
          {{ $t("point_expired") }}
          <span
            v-if="expiredDaysLeft !== null"
            class="vv-points-card__expiry-days"
          >
            ({{ expiredDaysLeft }} {{ $t("day") || "วัน" }})
          </span>
          <span v-else-if="expiredDate">
            {{ expiredDate }}
          </span>
        </span>
      </div>
      <div v-else class="vv-points-card__expiry vv-points-card__expiry--muted">
        &nbsp;
      </div>

      <button
        type="button"
        class="vv-points-card__redeem-btn"
        @click="goRedeem"
        :data-test="`${dataTest}-redeem-btn`"
      >
        {{ $t("redeem_profile_button") || "แลกแต้ม" }}
      </button>
    </div>
  </section>
</template>

<style lang="scss" scoped>
/*
  Vision Venture — Points Card
  - Figma frame 1:10745 "Card VV"
  - โครงสร้าง: 2 half stack (top glass + bottom glass) radius 24
  - สี/font: ใช้ token จาก src/style.css และ typography.css
  - accent สี Blue (Figma #23588E) ⇒ var(--primary-color)
  - Tier maintain/upgrade logic: mirror จาก ProfileTierLayout.vue
*/

.vv-points-card {
  display: flex;
  flex-direction: column;
  border-radius: 24px;
  background: var(
    --vv-points-card-background,
    var(--crm-card-glass-background)
  );
  -webkit-backdrop-filter: var(--crm-card-glass-backdrop-filter);
  backdrop-filter: var(--crm-card-glass-backdrop-filter);
  box-shadow: var(--crm-card-glass-box-shadow);
  overflow: hidden;
}

/* --- Flat variant ---
   Modern gradient ผูกกับสี tier ของ member (--vv-points-card-flat-start/end
   set จาก flatCardStyle) แทนพื้นขาวล้วนแบบเดิม — ใช้ใน /member, /reward,
   /point, all-privilege ฯลฯ */
.vv-points-card--flat {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-radius: 20px;
  background: linear-gradient(
    135deg,
    var(--vv-points-card-flat-start, var(--primary-color)) 0%,
    var(--vv-points-card-flat-end, var(--primary-color)) 100%
  );
  color: #fff;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.16);
}

.vv-points-card--flat .vv-points-card__number,
.vv-points-card--flat .vv-points-card__unit,
.vv-points-card--flat .vv-points-card__chevron {
  color: #fff;
}

.vv-points-card__flat-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.vv-points-card__flat-expiry {
  color: rgba(255, 255, 255, 0.85);
  font-size: var(--font-body);
  line-height: 1.4;
}

/* Pill โทนเดียวกับพื้นการ์ด (เกือบกลืนกัน) แค่ทึบขึ้นเล็กน้อยเพื่อแยกขอบ */
.vv-points-card__flat-tier {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  cursor: pointer;
  flex: 0 0 auto;
}

.vv-points-card__flat-tier-icon {
  width: 14px;
  height: 14px;
  object-fit: contain;
  flex: 0 0 auto;
}

.vv-points-card__flat-tier-name {
  color: #fff;
  font-size: var(--font-helper);
  font-weight: 600;
  line-height: 2;

  white-space: nowrap;
}

.vv-points-card__flat-tier-chevron {
  color: #fff;
  font-size: var(--font-caption);
  opacity: 0.85;
}

.vv-points-card__top {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  background: var(
    --vv-points-card-top-background,
    var(--crm-card-glass-background)
  );
  -webkit-backdrop-filter: var(--crm-card-glass-backdrop-filter);
  backdrop-filter: var(--crm-card-glass-backdrop-filter);
  border-bottom: 1px solid var(--primary-color-low);
  border-radius: 24px 24px 0 0;
}

.vv-points-card__main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.vv-points-card__points-row {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  cursor: pointer;
}

.vv-points-card__number {
  color: var(--primary-color);
  font-size: var(--font-heading);
  font-weight: 700;
  line-height: 1.1;
}

.vv-points-card__unit {
  color: var(--primary-color);
  font-size: var(--font-body);
  font-weight: 600;
  line-height: 1.4;
  padding-bottom: 2px;
}

.vv-points-card__chevron {
  color: var(--primary-color);
  font-size: var(--font-body);
  padding-bottom: 6px;
}

.vv-points-card__tier-hint {
  color: var(--text-color);
  font-size: var(--font-body);
  font-weight: 500;
  line-height: 1.4;
}

.vv-points-card__tier-hint-amount,
.vv-points-card__tier-hint-name {
  color: var(--primary-color);
  font-weight: 700;
}

/* --- Tier circular progress ring (Figma: donut ring + centered text) --- */
.vv-points-card__tier {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  cursor: pointer;
}

.vv-points-card__tier-ring {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
}

.vv-points-card__tier-ring-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.vv-points-card__tier-ring-track {
  stroke: var(--primary-color-low);
}

.vv-points-card__tier-ring-progress {
  transition: stroke-dasharray 0.4s ease;
}

/* วงใน ring กว้าง 42px (r 24 - stroke 3) — icon เต็มวงในพอดีขอบ ring */
.vv-points-card__tier-icon {
  position: relative;
  z-index: 1;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  object-fit: cover;
}

.vv-points-card__tier-name {
  position: relative;
  z-index: 1;
  font-size: var(--font-body);
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  /* วงใน ring กว้าง 42px (r 24 - stroke 3) — เผื่อขอบเหลือ ~36px ไม่ให้ชื่อล้นวง */
  display: block;
  max-width: 36px;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
}

.vv-points-card__tier-chevron {
  color: var(--text-color);
  font-size: var(--font-helper);
  opacity: 0.6;
}

/* --- Bottom half --- */
.vv-points-card__bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px;
  background: var(
    --vv-points-card-bottom-background,
    var(--crm-card-glass-background)
  );
  -webkit-backdrop-filter: var(--crm-card-glass-backdrop-filter);
  backdrop-filter: var(--crm-card-glass-backdrop-filter);
  border-radius: 0 0 24px 24px;
}

.vv-points-card__expiry {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  color: var(--text-color);
  font-size: var(--font-body);
  font-weight: 400;
  line-height: 1.2;
}

.vv-points-card__expiry-num {
  font-weight: 600;
}

.vv-points-card__coin {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--primary-color);
  color: var(--font-primary-color);
  font-size: var(--font-helper);
  flex: 0 0 auto;
}

.vv-points-card__expiry-text {
  display: inline;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Warning color for expiry countdown — Figma uses #D40407 red accent
   ไม่มี root token ตรง ๆ จึง hardcode */
.vv-points-card__expiry-days {
  color: var(--crm-danger-color);
  font-weight: 600;
}

.vv-points-card__redeem-btn {
  flex: 0 0 auto;
  height: 24px;
  padding: 2px 12px 4px;
  border: 0;
  border-radius: 21px;
  background: var(--button-color);
  color: var(--font-button-color);
  font-size: var(--font-body);
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
  cursor: pointer;
}
</style>
